import { toast } from 'sonner';
import { LoadingButton } from '@/components/LoadingButton';
import { Settings } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOrgs } from '@/contexts/OrgsContext';
import { useCreateOrgPortalSession } from '@/api/auth/billing/billing';
import { ComponentProps } from 'react';

interface CustomerPortalButtonProps {
  variant?: ComponentProps<typeof LoadingButton>['variant'];
  size?: ComponentProps<typeof LoadingButton>['size'];
  className?: string;
}

/**
 * Button that opens the Stripe Customer Portal for managing subscriptions,
 * viewing invoices, and updating payment methods.
 */
export function CustomerPortalButton({ variant = 'secondary', size, className }: CustomerPortalButtonProps) {
  const { t, language } = useLanguage();
  const { selectedOrganizationId } = useOrgs();
  const portalMutation = useCreateOrgPortalSession();

  const handleOpenCustomerPortal = async () => {
    if (!selectedOrganizationId) return;

    try {
      const response = await portalMutation.mutateAsync({
        orgId: selectedOrganizationId,
        data: {
          returnUrl: window.location.href,
          locale: language,
        },
      });

      if (response.data.portalUrl) {
        window.location.href = response.data.portalUrl;
      }
    } catch {
      toast.error(t.errors.common.unknown);
    }
  };

  return (
    <LoadingButton
      variant={variant}
      size={size}
      className={className}
      onClick={() => void handleOpenCustomerPortal()}
      isLoading={portalMutation.isPending}
    >
      <Settings className="h-4 w-4" />
      {t.billing.manageSubscription}
    </LoadingButton>
  );
}
